import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, MousePointerClick, UserPlus, ShoppingBag, Calendar, Mail, Search, Eye, Ban, Trash2, X, ChevronLeft, ChevronRight, Shield, Link2, BarChart3, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value ?? '-'}</p>
        <p className="text-xs text-[#71717A]">{label}</p>
      </div>
    </div>
  </div>
);

const PlatformAdmin = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [resetPwUserId, setResetPwUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const LIMIT = 20;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { params: { skip: page * LIMIT, limit: LIMIT, search: submittedSearch } });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  }, [page, submittedSearch]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setSubmittedSearch(search.trim());
  };

  const viewUser = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (err) {
      toast.error('Failed to load user details');
    }
  };

  const toggleDisable = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_disabled: !currentStatus });
      toast.success(currentStatus ? 'User enabled' : 'User disabled');
      fetchUsers();
      if (selectedUser === userId) viewUser(userId);
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete ${email} and ALL their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      setSelectedUser(null);
      setUserDetail(null);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${userId}`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setResetPwUserId(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Shield className="w-16 h-16 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
        <p className="text-[#71717A]">Only platform administrators can access this page.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="max-w-6xl" data-testid="platform-admin">
      <div className="mb-8">
        <h1 className="font-outfit font-bold text-2xl text-white flex items-center gap-2">
          <Shield className="w-6 h-6 text-violet-400" /> Platform Admin
        </h1>
        <p className="text-[#A1A1AA] mt-1">Manage all users and view platform analytics</p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total Users" value={stats.total_users} color="#8B5CF6" />
          <StatCard icon={UserPlus} label="Signups (30d)" value={stats.recent_signups_30d} color="#22C55E" />
          <StatCard icon={MousePointerClick} label="Total Clicks" value={stats.total_clicks} color="#3B82F6" />
          <StatCard icon={Eye} label="Profile Views" value={stats.total_views} color="#F59E0B" />
          <StatCard icon={Mail} label="Total Leads" value={stats.total_leads} color="#EC4899" />
          <StatCard icon={Calendar} label="Bookings" value={stats.total_bookings} color="#06B6D4" />
          <StatCard icon={ShoppingBag} label="Products" value={stats.total_products} color="#F97316" />
          <StatCard icon={Link2} label="Profiles" value={stats.total_profiles} color="#A855F7" />
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input
            data-testid="admin-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or username..."
            className="pl-10 bg-[#121212] border-[#27272A] text-white"
          />
        </div>
        <Button type="submit" className="bg-violet-500 hover:bg-violet-600 text-white">Search</Button>
      </form>

      {/* Users Table */}
      <div className="rounded-2xl bg-[#121212] border border-[#27272A] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-[#27272A]">
                <th className="text-left text-xs font-medium text-[#71717A] uppercase px-4 py-3">User</th>
                <th className="text-left text-xs font-medium text-[#71717A] uppercase px-4 py-3">Email</th>
                <th className="text-center text-xs font-medium text-[#71717A] uppercase px-4 py-3">Clicks</th>
                <th className="text-center text-xs font-medium text-[#71717A] uppercase px-4 py-3">Leads</th>
                <th className="text-center text-xs font-medium text-[#71717A] uppercase px-4 py-3">Links</th>
                <th className="text-left text-xs font-medium text-[#71717A] uppercase px-4 py-3">Joined</th>
                <th className="text-center text-xs font-medium text-[#71717A] uppercase px-4 py-3">Status</th>
                <th className="text-center text-xs font-medium text-[#71717A] uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#71717A]">
                  <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#71717A]">No users found</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className="border-b border-[#27272A]/50 hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.avatar_url ? (
                        <img src={resolveMediaUrl(u.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold">
                          {(u.username || u.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-white text-sm font-medium">{u.display_name || u.username}</p>
                        <p className="text-[#71717A] text-xs">@{u.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#A1A1AA]">{u.email}</td>
                  <td className="px-4 py-3 text-sm text-center text-white">{u.stats?.clicks || 0}</td>
                  <td className="px-4 py-3 text-sm text-center text-white">{u.stats?.leads || 0}</td>
                  <td className="px-4 py-3 text-sm text-center text-white">{u.stats?.links || 0}</td>
                  <td className="px-4 py-3 text-xs text-[#71717A]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-center">
                    {u.is_super_admin ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 text-xs font-medium">
                        <Shield className="w-3 h-3" /> Admin
                      </span>
                    ) : u.is_disabled ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">Disabled</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button data-testid={`view-user-${u.id}`} onClick={() => viewUser(u.id)} className="p-1.5 rounded-lg hover:bg-white/5" title="View details">
                        <Eye className="w-4 h-4 text-[#A1A1AA]" />
                      </button>
                      <button onClick={() => { setResetPwUserId(u.id); setNewPassword(''); }} className="p-1.5 rounded-lg hover:bg-violet-500/10" title="Reset password">
                        <KeyRound className="w-4 h-4 text-violet-400" />
                      </button>
                      {!u.is_super_admin && (
                        <>
                          <button onClick={() => toggleDisable(u.id, u.is_disabled)} className="p-1.5 rounded-lg hover:bg-yellow-500/10" title={u.is_disabled ? 'Enable' : 'Disable'}>
                            <Ban className={`w-4 h-4 ${u.is_disabled ? 'text-green-400' : 'text-yellow-400'}`} />
                          </button>
                          <button onClick={() => deleteUser(u.id, u.email)} className="p-1.5 rounded-lg hover:bg-red-500/10" title="Delete">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#27272A]">
            <p className="text-xs text-[#71717A]">Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 rounded-lg hover:bg-white/5 disabled:opacity-30">
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <span className="text-xs text-[#A1A1AA]">Page {page + 1} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 rounded-lg hover:bg-white/5 disabled:opacity-30">
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#121212] border border-[#27272A] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">

            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                {userDetail.profiles?.[0]?.avatar_url ? (
                  <img src={resolveMediaUrl(userDetail.profiles[0].avatar_url)} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-xl font-bold">
                    {(userDetail.username || '?')[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="text-white font-bold text-lg">{userDetail.profiles?.[0]?.display_name || userDetail.username}</h3>
                  <p className="text-[#71717A] text-sm">{userDetail.email}</p>
                  <p className="text-[#525252] text-xs">@{userDetail.username} &middot; Joined {new Date(userDetail.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <button onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
                <X className="w-5 h-5 text-[#71717A]" />
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div className="rounded-xl bg-[#1E1E1E] p-3 text-center">
                <p className="text-white font-bold text-lg">{userDetail.stats?.clicks || 0}</p>
                <p className="text-[#71717A] text-xs">Clicks</p>
              </div>
              <div className="rounded-xl bg-[#1E1E1E] p-3 text-center">
                <p className="text-white font-bold text-lg">{userDetail.stats?.views || 0}</p>
                <p className="text-[#71717A] text-xs">Views</p>
              </div>
              <div className="rounded-xl bg-[#1E1E1E] p-3 text-center">
                <p className="text-white font-bold text-lg">{userDetail.stats?.leads || 0}</p>
                <p className="text-[#71717A] text-xs">Leads</p>
              </div>
              <div className="rounded-xl bg-[#1E1E1E] p-3 text-center">
                <p className="text-white font-bold text-lg">{userDetail.stats?.bookings || 0}</p>
                <p className="text-[#71717A] text-xs">Bookings</p>
              </div>
            </div>

            {/* Profiles */}
            {userDetail.profiles?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-white font-semibold text-sm mb-3">Profiles ({userDetail.profiles.length})</h4>
                <div className="space-y-2">
                  {userDetail.profiles.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#1E1E1E]">
                      <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-xs font-bold">
                        {(p.display_name || p.username || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{p.display_name || p.username}</p>
                        <p className="text-[#71717A] text-xs">@{p.username}</p>
                      </div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/${p.username}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-violet-400 hover:text-violet-300">View Profile</a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Leads */}
            {userDetail.recent_leads?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-white font-semibold text-sm mb-3">Recent Leads ({userDetail.recent_leads.length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {userDetail.recent_leads.slice(0, 10).map((l, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-[#1E1E1E] text-sm">
                      <span className="text-white">{l.name || l.fields?.name || l.email || l.fields?.email || 'Anonymous'}</span>
                      <span className="text-[#71717A] text-xs">{l.source || 'contact'} &middot; {new Date(l.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {!userDetail.is_super_admin && (
              <div className="flex gap-3 pt-4 border-t border-[#27272A]">
                <Button
                  onClick={() => toggleDisable(userDetail.id, userDetail.is_disabled)}
                  variant="outline"
                  className={`flex-1 ${userDetail.is_disabled ? 'text-green-400 border-green-400/30 hover:bg-green-500/10' : 'text-yellow-400 border-yellow-400/30 hover:bg-yellow-500/10'}`}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {userDetail.is_disabled ? 'Enable User' : 'Disable User'}
                </Button>
                <Button
                  onClick={() => deleteUser(userDetail.id, userDetail.email)}
                  variant="outline"
                  className="flex-1 text-red-400 border-red-400/30 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setResetPwUserId(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#121212] border border-[#27272A] rounded-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-violet-400" /> Reset Password</h3>
              <button onClick={() => setResetPwUserId(null)}><X className="w-5 h-5 text-[#71717A]" /></button>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Resetting password for: <strong className="text-white">{users.find(u => u.id === resetPwUserId)?.email}</strong>
            </p>
            <Input
              data-testid="reset-pw-input"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 chars)"
              className="bg-[#1E1E1E] border-[#27272A] text-white mb-4"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') resetPassword(resetPwUserId); }}
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setResetPwUserId(null)} className="flex-1 bg-transparent border-[#27272A] text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={() => resetPassword(resetPwUserId)} className="flex-1 bg-violet-500 hover:bg-violet-600 text-white">Reset Password</Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PlatformAdmin;
